// Keep a Turbo frame's history "promotion" from cancelling the navigation the
// user actually asked for.
//
// THE MECHANISM
//
// A turbo frame that carries data-turbo-action ("advance" here) does not update
// the address bar by itself. It finishes a frame navigation by proposing a
// whole-page Turbo Drive visit to the frame's own URL — see
// FrameController#proposeVisitIfNavigatedWithAction and #loadFrameResponse in
// @hotwired/turbo. That proposal is the LAST thing #loadFrameResponse does:
//
//     this.changeHistory()            // address bar already updated here
//     await this.view.render(...)     // new rows already painted here
//     session.frameRendered(...)
//     session.frameLoaded(...)        // turbo:frame-load fires here
//     await this.fetchResponseLoaded(...)   // ...and the promotion visit here
//
// Turbo's Navigator.startVisit() begins with stop(), which cancels whatever
// visit is currently in flight. So a Drive visit the user started after the new
// rows appeared — a nav-bar link clicked just after the filtered list came
// back — is silently aborted: its fetch is cancelled (responseStart 0), nothing
// renders, and the progress bar is left running on the page they tried to
// leave. The click is simply lost. Clicking again works, which is why this
// reads as "the app ignored me" rather than as a crash.
//
// THE WINDOW, MEASURED
//
// It opens when the frame's DOM is swapped and closes when the promotion is
// proposed. FrameRenderer.render() awaits nextRepaint() twice after swapping
// the frame in, so it is two animation frames wide: 32.7ms and 33.9ms on two
// instrumented runs of the events index (issue #357, Turbo 8.0.20 — the
// version vendored here, and the code above is byte-identical in 8.0.23, so
// this is not fixed upstream).
//
// THE RULE
//
// The promotion exists to seed Turbo's snapshot cache so the Back button can
// restore the filtered page instantly. When the user has already asked to go
// somewhere else, that is worth less than the navigation they asked for, so the
// promotion is the one that gets dropped. Back still works — changeHistory()
// pushed the entry before any of this — it just refetches instead of restoring
// from cache.
//
// A promotion is recognised by what it IS: a whole-page visit proposing the URL
// of a frame that has just navigated. Every frame event that names the frame
// carries frame.src — the raw attribute Turbo itself passes to session.visit()
// — so the pairing is exact and needs nothing from Turbo's internals. A
// recorded URL is consumed by the first visit that matches it.
//
// A click always wins that comparison, because a frame's URL is very often a
// URL the user can also click: the pipeline segment bar's "All" navigates the
// events_list frame to /events, and the nav bar's EVENTS link goes to /events
// too. Turbo tells the two apart for us, if the question is asked at the right
// moment. A followed link runs
//
//     LinkClickObserver#clickBubbled        (the real click event)
//       → Session#willFollowLinkToLocation  → turbo:click
//       → Session#followedLinkToLocation    → session.visit()
//       → Navigator#proposeVisit            → turbo:before-visit
//
// with no await anywhere, so a user navigation's turbo:before-visit is dispatched
// INSIDE its own turbo:click. A promotion's is not: it arrives a whole fetch
// later. Recording the clicked URL and dropping it again on the next microtask
// therefore separates them exactly. Links a frame intercepts fire turbo:click
// as well, which costs nothing — their promotion still arrives long after the
// microtask. The clicked URL is deliberately NOT consumed from the recorded
// set: the frame's own promotion for that URL may still be on its way, and it
// still has to be recognised when it lands.
//
// It deliberately does NOT ask where the address bar is. That was the first
// version of this rule, and it holds only from a COLD snapshot cache. When the
// destination is already cached, the user's own visit renders the preview and
// calls changeHistory() first (BrowserAdapter#visitStarted →
// Visit#loadCachedSnapshot → PageView#renderPage → Visit#changeHistory), so the
// promotion proposes the frame's URL while the document already reads the
// destination — indistinguishable, by location alone, from the user navigating.
// The promotion was therefore let through, its startVisit() cancelled the real
// fetch, and the user was left on a cached preview that never updated. Issue
// #390: CI failed 4 of 5 runs on development that way.
//
// This is document-level and therefore covers every promoting surface, not just
// the events index: the clients and suppliers search forms carry
// data: { turbo_action: "advance" } too.
//
// KNOWN LIMIT, one each way.
//
// A visit that is NOT a click — Turbo.visit(), a form's redirect, or a link
// carrying data-turbo-method / data-turbo-stream — to a URL a frame has just
// navigated to, proposed while a different visit is in flight, is still dropped
// as a promotion. A frame that navigates and never promotes leaves its URL
// recorded until the next turbo:load, and nothing at frame-navigation time says
// whether a promotion is coming (the action can be carried by the clicked link
// rather than by the frame), so the recorded set cannot be narrowed. Note this
// window is NOT the ~33ms above: it is however long the other visit's fetch
// takes. The click discriminator is what removes the everyday version of this —
// before it, clicking EVENTS while a /suppliers visit was still loading lost
// the click, because the segment bar had already recorded /events.
//
// The third of those is the one to watch, because it is a link and reads like
// one. FormLinkClickObserver sits on document.documentElement and so bubbles
// before the LinkClickObserver on window; it preventDefault()s the click and
// submits a synthesised form instead, and its willFollowLinkToLocation asks
// willSubmitFormLinkToLocation, which dispatches no turbo:click at all. None of
// those links is on a promoting surface today — the only non-click visit
// reachable from the events, clients or suppliers index is the account menu's
// sign-out button_to — so this is a note for whoever adds the first one.
//
// And the other way: a frame-targeted FORM submission whose frame is detached
// before turbo:frame-load is recorded by NEITHER half — before-fetch-request
// names the <form>, which has no src, and frame-load is redirected onto <html>,
// which has none either. That is the clients and suppliers search boxes in the
// warm-cache race, where their promotion goes through and can still cancel the
// navigation the user asked for. Stated rather than fixed: it needs a third
// source for the frame's URL, which is a larger change than this one.

// The URL of the in-flight visit that must not be cancelled, or null. Only a
// real navigation is ever stored here — a promotion is not something to
// protect, so two promotions racing each other still resolve the way Turbo
// intends.
let protectedVisitUrl = null

// URLs of frames that have navigated and may be about to propose a promotion
// for themselves. Entries are consumed on match and dropped wholesale at
// turbo:load.
const loadedFrameUrls = new Set()

// Two events, because neither one alone always names the frame.
//
// turbo:frame-load is the natural one and is what a frame-targeted FORM
// submission gives us (the clients and suppliers search forms): its
// turbo:before-fetch-request is dispatched on the <form>, which has no src.
// But turbo:frame-load is dispatched on the frame only while the frame is still
// in the document — Turbo's dispatch() sends an event for a disconnected target
// to document.documentElement instead (`if (target && target.isConnected)`),
// and <html> has no src either. A frame detached before it finished loading is
// not a corner case here: it is exactly the warm-cache race this guard exists
// for, where the destination's cached preview has already replaced the body.
// Measured in the system test — without the fetch-request half, that case
// records nothing and the promotion goes straight through.
//
// turbo:before-fetch-request covers it, because the frame is unquestionably
// connected at the moment its own src change starts the fetch.
function onFrameSource(event) {
  const src = event.target && event.target.src
  if (!src) return

  loadedFrameUrls.add(new URL(src, document.baseURI).href)
}

// The URL of the link currently being followed, for the length of that click's
// own dispatch and no longer. Cleared on a microtask rather than at
// turbo:before-visit, so that a click Turbo decides NOT to visit (one a frame
// intercepts, one to an unvisitable location) cannot leave it set.
let clickedUrl = null

function onClick(event) {
  clickedUrl = event.detail.url
  queueMicrotask(() => {
    clickedUrl = null
  })
}

function onBeforeVisit(event) {
  const proposedUrl = event.detail.url
  // A visit proposed inside a click's own dispatch is that click, whatever the
  // recorded set says — and && leaves the entry in the set for the promotion
  // that is still coming. Otherwise Set#delete answers "was it there" and
  // consumes it in one step, so a frame URL can only be mistaken once.
  const isClick = proposedUrl === clickedUrl
  const isPromotion = !isClick && loadedFrameUrls.delete(proposedUrl)

  if (isPromotion) {
    // Any protected visit outranks a promotion, including one going to the very
    // URL the promotion proposes: startVisit() would stop() the user's fetch and
    // replace it with a willRender:false visit that paints nothing, leaving the
    // page the user was trying to refresh exactly as it was.
    if (protectedVisitUrl) event.preventDefault()
    return
  }

  // turbo:before-visit is the last chance to stop a visit, so anything not
  // cancelled here is about to start.
  protectedVisitUrl = proposedUrl
}

// Any visit that starts cancels whatever was in flight (Navigator#startVisit
// begins with stop()), so the protected URL cannot outlive one — otherwise a
// visit that was itself cancelled leaves this pointing at a navigation nobody
// is waiting for, and the next promotion is dropped for nothing. The protected
// visit's own turbo:visit must not clear it, or there is nothing left to
// protect by the time the promotion arrives. Visits that never propose
// themselves reach here without a turbo:before-visit — a Back button restore is
// the everyday one — which is exactly the case turbo:load cannot cover.
function onVisit(event) {
  if (event.detail.url !== protectedVisitUrl) protectedVisitUrl = null
}

function onLoad() {
  protectedVisitUrl = null
  loadedFrameUrls.clear()
}

// A form submission cancels the in-flight visit (Navigator#submitForm starts
// with stop()) without dispatching turbo:before-visit and without a turbo:visit
// of its own, so nothing above sees it end and turbo:load never comes for it.
function onSubmitStart() {
  protectedVisitUrl = null
}

let started = false

// Registered once, on the document, so it survives Turbo Drive navigation —
// there is no per-page setup to redo and nothing to unbind.
export function startFramePromotionGuard() {
  if (started) return
  started = true

  document.addEventListener("turbo:click", onClick)
  document.addEventListener("turbo:before-fetch-request", onFrameSource)
  document.addEventListener("turbo:frame-load", onFrameSource)
  document.addEventListener("turbo:before-visit", onBeforeVisit)
  document.addEventListener("turbo:visit", onVisit)
  document.addEventListener("turbo:load", onLoad)
  document.addEventListener("turbo:submit-start", onSubmitStart)
}

// Test seam: the module's state is module-level and jsdom keeps it between
// cases. Not used by the application.
export function resetFramePromotionGuardForTest() {
  document.removeEventListener("turbo:click", onClick)
  document.removeEventListener("turbo:before-fetch-request", onFrameSource)
  document.removeEventListener("turbo:frame-load", onFrameSource)
  document.removeEventListener("turbo:before-visit", onBeforeVisit)
  document.removeEventListener("turbo:visit", onVisit)
  document.removeEventListener("turbo:load", onLoad)
  document.removeEventListener("turbo:submit-start", onSubmitStart)
  protectedVisitUrl = null
  loadedFrameUrls.clear()
  clickedUrl = null
  started = false
}
